import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

/**
 * Switch — high-contrast across ALL mood themes.
 *
 * Feb 27, 2026 (per provider screenshot): same bug shape as Checkbox —
 * `data-[state=checked]:bg-primary` was indistinguishable from
 * `data-[state=unchecked]:bg-input` in several mood themes (calm-gold,
 * meadow, sunset). Providers couldn't tell at a glance which toggles
 * were on.
 *
 * Fix: hard-coded `bg-emerald-500` (on) vs `bg-slate-700` (off), plus a
 * bright white thumb with a subtle shadow for depth. Independent of
 * theme. Same h-5 w-9 footprint so existing layouts don't shift.
 */

const Switch = React.forwardRef(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-emerald-500 data-[state=checked]:shadow-md data-[state=checked]:shadow-emerald-500/40",
      "data-[state=unchecked]:bg-slate-700 data-[state=unchecked]:border-slate-500/40",
      className
    )}
    {...props}
    ref={ref}>
    <SwitchPrimitives.Thumb
      className={cn(
        "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-md ring-0 transition-transform",
        "data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
      )} />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
